// The locked page (PRIVACY_PLAN.md §4.1): what a visitor who may not open a
// club, competition, match or profile is told, what they can do about it, and
// which harmless facts the stub still lets the page show. Pure functions so the
// copy and the action order are unit-tested; PrivateNotice.vue renders them.
//
// Mirrors the app's TlPrivateState — change both together.

const FORMAT_LABELS = {
  league: 'Box league',
  ladder: 'Ladder',
  tournament: 'Tournament',
  meet: 'Meet',
  americano: 'Americano league',
};

/**
 * How often a meet runs, from the API's recurrenceMode (0 one-off / 1 on a
 * rule / 2 dates added by hand), recurrenceUnit (0 week / 1 month) and
 * recurrenceInterval.
 */
export function meetCadence(c) {
  if (!c) return '';
  if (c.recurrenceMode === 0) return 'One-off';
  if (c.recurrenceMode === 2) return 'Dates added by the club';
  if (c.recurrenceMode !== 1) return '';
  const n = c.recurrenceInterval > 0 ? c.recurrenceInterval : 1;
  if (c.recurrenceUnit === 1) return n === 1 ? 'Monthly' : `Every ${n} months`;
  return n === 1 ? 'Weekly' : `Every ${n} weeks`;
}

/**
 * Title, body and an optional hint for the notice.
 * @param {{reason:string, entity?:string, name?:string, clubName?:string, signedIn:boolean, followStatus?:string|null, unlisted?:boolean, managed?:boolean, playersLockedLine?:string|null}} o
 */
export function privateCopy(o) {
  const name = (o.name || '').trim();
  const entity = o.entity || 'competition';
  const club = (o.clubName || '').trim();
  switch (o.reason) {
    case 'club':
      // Members only AND not listed (PRIVACY_PLAN.md D5): name and logo, nothing
      // else, not even an invitation to ask. A visible join link still gets Join.
      if (o.unlisted) {
        return {title: name ? `${name} is private` : 'This club is private', body: '', hint: ''};
      }
      return {
        title: name ? `${name} is only open to its players` : 'This club is only open to its players',
        body: o.signedIn
            ? 'Only players at the club can see the player list, competitions and results. Join to take part.'
            : 'Players at the club can sign in to see the player list, competitions and results. Not in the club yet? You can ask to join.',
        hint: '',
      };
    case 'fixture':
      return {
        title: 'This match is only open to club players',
        body: o.signedIn
            ? (club ? `Only players at ${club} can see who played and the score.` : 'Only players at the club can see who played and the score.')
            : (club ? `Players at ${club} can sign in to see who played and the score.` : 'Players at the club can sign in to see who played and the score.'),
        hint: '',
      };
    case 'player': {
      const title = name ? `${name}'s profile is private` : "This player's profile is private";
      if (o.followStatus === 'following') return {title, body: '', hint: ''};
      // D47: they share a club, but its players need a membership the viewer hasn't got (TEST_T4 M5).
      if (o.signedIn && o.playersLockedLine) {
        return {title, body: `${o.playersLockedLine.replace(/\.$/, '')}.`, hint: o.followStatus === 'none' ? 'Or follow them. They will need to accept.' : ''};
      }
      if (!o.signedIn) {
        return {title, body: 'People in their clubs can see it. Sign in if you are one of them.', hint: ''};
      }
      if (o.followStatus === 'requested') {
        // A managed player has no account: a club admin answers for them (§3.6).
        return {title, body: 'People in their clubs can see it.', hint: o.managed
              ? 'A club admin will need to approve your follow request before you can see their profile.'
              : 'They will need to accept your follow request before you can see their profile.'};
      }
      if (o.followStatus === 'requestReceived') {
        return {title, body: 'They asked to follow you. Accept and you can both see each other.', hint: ''};
      }
      if (o.followStatus === 'none') {
        return {title, body: 'People in their clubs can see it.', hint: 'Follow to see their profile. They will need to accept.'};
      }
      return {title, body: 'People in their clubs can see it.', hint: ''};
    }
    default:
      return {
        title: name ? `${name} is only open to club players` : `This ${entity} is only open to club players`,
        body: o.signedIn
            ? (club ? `Only players at ${club} can see the standings, matches and results.` : 'Only club players can see the standings, matches and results.')
            : (club ? `Players at ${club} can sign in to see the standings, matches and results.` : 'Club players can sign in to see the standings, matches and results.'),
        hint: '',
      };
  }
}

/**
 * The buttons, in order. The first one is the primary action: a signed-out
 * visitor is most likely a member, so Sign in leads; a signed-in non-member
 * is offered the way in (Join or Request to join); on a profile, Follow.
 * @returns {Array<{key:string,label:string,primary:boolean,to?:any,icon:string,disabled?:boolean}>}
 */
export function privateActions(o) {
  const actions = [];
  const join = o.joinSlug
      ? {key: 'join', label: o.joinLabel || 'Join', primary: true, to: '/invite/' + o.joinSlug, icon: 'mdi-account-plus'}
      : o.requestToJoin
          ? {key: 'request', label: 'Request to join', primary: true, icon: 'mdi-account-plus'}
          : null;
  if (!o.signedIn) {
    actions.push({key: 'signIn', label: 'Sign in', primary: true, to: o.loginTo, icon: 'mdi-login'});
    if (join && o.reason !== 'player') actions.push({...join, primary: false});
    return actions;
  }
  if (o.reason === 'player') {
    if (['none', 'requested', 'requestReceived'].includes(o.followStatus)) {
      actions.push({
        key: 'follow',
        label: o.followStatus === 'requested' ? 'Requested' : o.followStatus === 'requestReceived' ? 'Accept their request' : 'Follow',
        primary: o.followStatus !== 'requested',
        icon: 'mdi-account-heart-outline',
        disabled: o.followStatus === 'requested',
      });
    }
    return actions;
  }
  if (join) actions.push(join);
  return actions;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || one + 's')}`;

/** Facts for a locked club page, from ClubPageResponse.summary. */
export function clubFacts(summary, club) {
  if (!summary) return [];
  const facts = [];
  const members = summary.members ?? club?.playerCount ?? 0;
  if (members > 0) facts.push({key: 'members', icon: 'mdi-account-group', value: String(members), label: members === 1 ? 'player' : 'players'});
  const comps = [
    [summary.leagues, 'box league'],
    [summary.ladders, 'ladder'],
    [summary.tournaments, 'tournament'],
    [summary.meets, 'meet'],
    [summary.americanos, 'Americano league'],
  ].filter(([n]) => n > 0);
  const total = comps.reduce((a, [n]) => a + n, 0);
  if (total > 0) {
    facts.push({
      key: 'competitions',
      icon: 'mdi-medal-outline',
      value: String(total),
      label: total === 1 ? 'competition' : 'competitions',
      detail: comps.map(([n, name]) => plural(n, name)).join(' · '),
    });
  }
  if ((summary.matchesPlayed ?? 0) > 0) {
    facts.push({key: 'matches', icon: 'mdi-scoreboard', value: String(summary.matchesPlayed), label: summary.matchesPlayed === 1 ? 'match played' : 'matches played'});
  }
  if (summary.sports?.length) {
    facts.push({key: 'sports', icon: 'mdi-tennis', value: summary.sports.join(', '), label: summary.sports.length === 1 ? 'sport' : 'sports', text: true});
  }
  const since = summary.memberSince ? new Date(summary.memberSince) : null;
  if (since && !Number.isNaN(since.getTime())) {
    facts.push({key: 'since', icon: 'mdi-calendar-check', value: String(since.getFullYear()), label: 'on Tap Leagues since'});
  }
  return facts;
}

/**
 * Facts for a locked competition page from its stub. `kind` is the format key;
 * `formatDate` turns an ISO string into a short date ("18 Nov 2023").
 */
export function competitionFacts(kind, c, formatDate = (d) => d) {
  if (!c) return [];
  const facts = [];
  const sport = c.sport?.name || c.sportName;
  if (sport) facts.push({key: 'sport', icon: 'mdi-tennis', value: sport, label: 'sport', text: true});
  if (FORMAT_LABELS[kind]) facts.push({key: 'format', icon: 'mdi-medal-outline', value: FORMAT_LABELS[kind], label: 'format', text: true});
  const now = new Date();
  switch (kind) {
    case 'ladder': {
      if (c.teamCount > 0) facts.push({key: 'players', icon: 'mdi-account-group', value: String(c.teamCount), label: c.playersPerTeam > 1 ? (c.teamCount === 1 ? 'team' : 'teams') : (c.teamCount === 1 ? 'player' : 'players')});
      pushDates(facts, c.startDate, c.endDate, now, formatDate);
      break;
    }
    case 'league': {
      if (c.cycleCount > 0) facts.push({key: 'cycles', icon: 'mdi-refresh', value: String(c.cycleCount), label: c.cycleCount === 1 ? 'season played' : 'seasons played'});
      if (c.latestCycleTeamCount > 0) facts.push({key: 'players', icon: 'mdi-account-group', value: String(c.latestCycleTeamCount), label: c.playersPerTeam > 1 ? 'teams this season' : 'players this season'});
      pushDates(facts, c.latestCycleStartDate, c.latestCycleEndDate, now, formatDate, 'Latest season');
      break;
    }
    case 'americano': {
      if (c.cycleCount > 0) facts.push({key: 'cycles', icon: 'mdi-refresh', value: String(c.cycleCount), label: c.cycleCount === 1 ? 'round played' : 'rounds played'});
      if (c.latestCyclePlayerCount > 0) facts.push({key: 'players', icon: 'mdi-account-group', value: String(c.latestCyclePlayerCount), label: 'players this round'});
      pushDates(facts, c.latestCycleStartDate, c.latestCycleEndDate, now, formatDate, 'Latest round');
      break;
    }
    case 'tournament': {
      if (c.teamCount > 0) facts.push({key: 'players', icon: 'mdi-account-group', value: String(c.teamCount), label: c.playersPerTeam > 1 ? 'teams' : 'players'});
      pushDates(facts, c.startDate, c.endDate, now, formatDate);
      break;
    }
    case 'meet': {
      const rec = meetCadence(c);
      if (rec) facts.push({key: 'when', icon: 'mdi-calendar-repeat', value: rec, label: 'how often', text: true});
      if (c.firstOccursAt) {
        const first = new Date(c.firstOccursAt);
        facts.push({key: 'first', icon: 'mdi-calendar', value: formatDate(c.firstOccursAt), label: first < now ? 'running since' : 'first date', text: true});
      }
      if (c.maxAttendees > 0) facts.push({key: 'places', icon: 'mdi-account-group', value: String(c.maxAttendees), label: 'places a session'});
      break;
    }
  }
  if (c.scoringSystemName) facts.push({key: 'scoring', icon: 'mdi-counter', value: c.scoringSystemName, label: 'scoring', text: true});
  return facts;
}

function pushDates(facts, start, end, now, formatDate, prefix) {
  if (!start) return;
  const s = new Date(start);
  if (Number.isNaN(s.getTime()) || s.getFullYear() < 1900) return;
  const started = s < now;
  const e = end ? new Date(end) : null;
  const ended = e && !Number.isNaN(e.getTime()) && e < now;
  let label;
  if (prefix) label = prefix.toLowerCase();
  else label = ended ? 'ran from' : started ? 'running since' : 'starts';
  const value = (e && !Number.isNaN(e.getTime()) && e.getFullYear() > 1900 && (ended || prefix))
      ? `${formatDate(start)} – ${formatDate(end)}`
      : formatDate(start);
  facts.push({key: 'dates', icon: 'mdi-calendar', value, label, text: true});
}

/** Facts for a locked match page from its stub. */
export function fixtureFacts(f, formatDateTime = (d) => d) {
  if (!f) return [];
  const facts = [];
  const competition = f.leagueName || f.ladderName || f.tournamentName || f.meetName || f.americanoName;
  if (competition) {
    const detail = [f.leagueCycleName, f.leagueCycleDivisionName, f.americanoCycleName, f.tournamentStageName].filter(Boolean).join(' · ');
    facts.push({key: 'competition', icon: 'mdi-medal-outline', value: competition, label: detail || 'competition', text: true});
  }
  if (f.sport?.name) facts.push({key: 'sport', icon: 'mdi-tennis', value: f.sport.name, label: 'sport', text: true});
  const when = f.playedAt || f.scheduledAt;
  if (when) facts.push({key: 'when', icon: 'mdi-calendar-clock', value: formatDateTime(when), label: f.playedAt ? 'played' : 'scheduled', text: true});
  return facts;
}
